import React, { useEffect, useState } from "react";
import axios from "axios";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import { ONBOARDING_GET_API } from "../../api/onboarding";

const OnboardedUsersTable = () => {
  const [onboardedUsers, setOnboardedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "success",
  });

  const fetchOnboardedUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(ONBOARDING_GET_API);
      // Assuming response.data is an array of onboarding documents
      setOnboardedUsers(response.data || []);
    } catch (error) {
      console.error("Failed to fetch onboarding data", error);
      setSnackbar({
        open: true,
        message: "Failed to load onboarding data",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOnboardedUsers();
  }, []);

  // Define columns matching your schema fields
  const columns = [
    { field: "_id", headerName: "ID", width: 220 },
    {
      field: "userId",
      headerName: "User ID",
      width: 220,
      // userId is an ObjectId, show string
      valueGetter: (params) =>
        params.row.userId ? params.row.userId.toString() : "-",
    },
    {
      field: "purpose",
      headerName: "Purpose",
      width: 180,
      valueGetter: (params) => params.row.purpose || "-",
    },
    {
      field: "preferredCategories",
      headerName: "Preferred Categories",
      width: 220,
      valueGetter: (params) =>
        Array.isArray(params.row.preferredCategories)
          ? params.row.preferredCategories.join(", ")
          : "-",
    },
    {
      field: "openToStartupOrScholarship",
      headerName: "Open to Startup/Scholarship",
      width: 200,
      type: "boolean",
      valueGetter: (params) =>
        typeof params.row.openToStartupOrScholarship === "boolean"
          ? params.row.openToStartupOrScholarship
          : false,
    },
    { field: "fullName", headerName: "Full Name", width: 150 },
    { field: "contactNumber", headerName: "Contact Number", width: 150 },
    { field: "degree", headerName: "Degree", width: 150 },
    { field: "organization", headerName: "Organization", width: 150 },
    { field: "linkedin", headerName: "LinkedIn", width: 250 },
    {
      field: "onboardingCompleted",
      headerName: "Onboarding Completed",
      width: 180,
      type: "boolean",
      valueGetter: (params) =>
        typeof params.row.onboardingCompleted === "boolean"
          ? params.row.onboardingCompleted
          : false,
    },
    {
      field: "createdAt",
      headerName: "Created At",
      width: 180,
      type: "dateTime",
      valueGetter: (params) =>
        params.row.createdAt ? new Date(params.row.createdAt) : null,
      valueFormatter: (params) =>
        params.value ? params.value.toLocaleString() : "-",
    },
    {
      field: "updatedAt",
      headerName: "Updated At",
      width: 180,
      type: "dateTime",
      valueGetter: (params) =>
        params.row.updatedAt ? new Date(params.row.updatedAt) : null,
      valueFormatter: (params) =>
        params.value ? params.value.toLocaleString() : "-",
    },
  ];

  // Map rows to include required id field for DataGrid
  const rows = onboardedUsers.map((user) => ({
    ...user,
    id: user._id, // DataGrid needs an 'id' attribute
  }));

  return (
    <Box p={3}>
      <Typography variant="h5" mb={2}>
        Onboarded Users
      </Typography>

      <Button variant="outlined" onClick={fetchOnboardedUsers} sx={{ mb: 2 }}>
        Refresh
      </Button>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={2}>
          <CircularProgress />
        </Box>
      ) : (
        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={8}
          rowsPerPageOptions={[8, 15, 25]}
          autoHeight
          sx={{ bgcolor: "#fff", borderRadius: 2, boxShadow: 2 }}
          disableSelectionOnClick
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.type}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OnboardedUsersTable;
